import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, AlertCircle, Download, Upload } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { formatINR, formatDate } from '../lib/format';
import { toCSV, downloadCSV, parseCSV, isoToFY, currentFY, listRecentFYs, fyRange } from '../lib/csv';

export default function Invoices() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [fy, setFy] = useState('all');
  const [importOpen, setImportOpen] = useState(false);
  const load = () => api.get('/invoices').then(r => setItems(r.data.filter(x => !x.deleted_at)));
  useEffect(() => { load(); }, []);

  const rangeFilter = (i) => {
    if (fy === 'all') return true;
    const r = fyRange(fy);
    if (!r) return true;
    const iso = i.issued_on || i.event_date || '';
    return iso >= r.start && iso <= r.end;
  };

  const filtered = items.filter(i => (!q || (i.number + ' ' + i.client_name).toLowerCase().includes(q.toLowerCase())) && rangeFilter(i));
  const totalBooked = filtered.reduce((s,x) => s + (x.total||0), 0);
  const totalCollected = filtered.reduce((s,x) => s + (x.advance_received||0) + (x.payments||[]).reduce((a,p) => a + (p.amount||0), 0), 0);

  const exportCSV = () => {
    const cols = [
      { key: 'number', label: 'Invoice #' },
      { key: 'client_name', label: 'Client' },
      { key: 'event_date', label: 'Event Date' },
      { key: 'venue', label: 'Venue' },
      { key: 'reporting_time', label: 'Reporting Time' },
      { key: 'ready_by_time', label: 'Ready By' },
      { key: 'total', label: 'Total (INR)' },
      { key: 'advance_received', label: 'Advance (INR)' },
      { key: 'balance', label: 'Balance (INR)' },
      { key: 'status', label: 'Status' },
      { key: 'value_given_away', label: 'Value Given Away (INR)' },
      { label: 'FY', get: x => isoToFY(x.issued_on || x.event_date) || '' },
      { key: 'issued_on', label: 'Issued On' },
    ];
    downloadCSV(`invoices-${fy === 'all' ? 'all' : fy}-${new Date().toISOString().slice(0,10)}.csv`, toCSV(filtered, cols));
    toast.success(`Exported ${filtered.length} invoices`);
  };

  return (
    <div className="px-5 py-5 lg:px-10 lg:py-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div><div className="eyebrow">Money</div><h1 className="serif text-[28px]">Invoices</h1></div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={exportCSV} className="btn-ghost text-xs" data-testid="inv-export"><Download size={12}/> Export CSV</button>
          <button onClick={() => setImportOpen(true)} className="btn-ghost text-xs" data-testid="inv-import"><Upload size={12}/> Import</button>
          <Link to="/invoices/new" className="btn-gold" data-testid="invoice-new-btn"><Plus size={16}/> New</Link>
        </div>
      </div>

      <div className="flex gap-2 mb-4 items-center flex-wrap">
        <input placeholder="Search…" value={q} onChange={e => setQ(e.target.value)} className="flex-1 min-w-[180px]" data-testid="invoice-search"/>
        <select value={fy} onChange={e => setFy(e.target.value)} className="w-40" data-testid="invoice-fy">
          <option value="all">All FYs</option>
          {listRecentFYs(6).map(f => <option key={f} value={f}>{f}{f === currentFY() ? ' (current)' : ''}</option>)}
        </select>
      </div>

      <div className="card-alt p-3 mb-4 flex justify-between text-sm">
        <div><span className="eyebrow">{filtered.length} invoice{filtered.length===1?'':'s'}</span></div>
        <div className="flex gap-4">
          <div><span className="eyebrow mr-2">Booked</span><span className="mono-num serif">{formatINR(totalBooked)}</span></div>
          <div><span className="eyebrow mr-2">Collected</span><span className="mono-num serif" style={{color:'var(--ok)'}}>{formatINR(totalCollected)}</span></div>
        </div>
      </div>

      <div className="space-y-2">
        {filtered.map(i => (
          <Link to={`/invoices/${i.id}`} key={i.id} className="card-cream p-4 block hover:bg-[var(--cream-alt)]" data-testid={`invoice-${i.id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="flex items-center gap-2">
                  <div className="serif text-[19px]">{i.number}</div>
                  {i.needs_confirmation && <span className="needs-conf-badge"><AlertCircle size={11}/> Needs confirmation</span>}
                </div>
                <div className="text-xs text-[var(--muted)] mt-1">{i.client_name} · {formatDate(i.event_date)} · {isoToFY(i.issued_on || i.event_date)}</div>
              </div>
              <div className="text-right">
                <div className="serif mono-num">{formatINR(i.total)}</div>
                <span className={`status-pill status-${i.status==='fully_paid'?'ok':i.status==='overdue'?'overdue':i.status==='partially_paid'?'pending':'draft'}`}>
                  {i.status==='awaiting_advance'?'Awaiting Advance':i.status.replace('_',' ')}
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {importOpen && <ImportModal onClose={() => setImportOpen(false)} onDone={() => { setImportOpen(false); load(); }}/>}
    </div>
  );
}

function ImportModal({ onClose, onDone }) {
  const [rows, setRows] = useState(null);
  const [creating, setCreating] = useState(false);

  const template = `Invoice #,Client,Event Date,Venue,Reporting Time,Ready By,Total (INR),Advance (INR),Status\nINV-2010,Sample Bride,2025-11-14,Taj Palace Delhi,04:00 AM,08:00 AM,45000,13500,partially_paid`;

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const parsed = parseCSV(text);
    setRows(parsed.rows);
  };
  const importAll = async () => {
    setCreating(true);
    let ok = 0, failed = 0;
    for (const r of rows) {
      try {
        const total = parseFloat(r['Total (INR)'] || r['Total'] || 0);
        const advance = parseFloat(r['Advance (INR)'] || r['Advance'] || 0);
        const balance = Math.max(total - advance, 0);
        const body = {
          number: r['Invoice #'] || r['Invoice Number'] || '',
          client_name: r['Client'] || r['Client Name'] || '',
          event_date: r['Event Date'] || '',
          venue: r['Venue'] || '',
          reporting_time: r['Reporting Time'] || '',
          ready_by_time: r['Ready By'] || '',
          services: [{ name: 'Historical (imported)', qty: 1, rate: total, was: null }],
          subtotal: total, total, discount: 0, advance_received: advance, balance,
          issued_on: r['Issued On'] || r['Event Date'] || new Date().toISOString().slice(0,10),
          status: r['Status'] || (balance <= 0 ? 'fully_paid' : advance > 0 ? 'partially_paid' : 'awaiting_advance'),
        };
        await api.post('/invoices', body);
        ok++;
      } catch (e) { failed++; }
    }
    toast.success(`Imported ${ok} invoices${failed?` · ${failed} failed`:''}`);
    setCreating(false);
    onDone();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center bg-black/40 p-0 lg:p-6">
      <div className="w-full max-w-2xl card-cream p-6 overflow-y-auto max-h-[92vh]">
        <div className="flex items-center justify-between mb-4"><h2 className="serif text-[22px]">Import past invoices</h2><button onClick={onClose}>×</button></div>
        <div className="text-xs text-[var(--muted)] mb-3">Columns supported: <b>Invoice #, Client, Event Date, Venue, Reporting Time, Ready By, Total (INR), Advance (INR), Status</b>. Existing rows with the same number won't be overwritten — invoice numbers auto-generate if left blank.</div>
        <button className="btn-ghost text-xs mb-3" onClick={() => downloadCSV('invoice-template.csv', template)}>Download template</button>
        <input type="file" accept=".csv,text/csv" onChange={onFile} data-testid="inv-import-file"/>
        {rows && (
          <div className="mt-4">
            <div className="text-sm mb-2">Preview — {rows.length} row{rows.length===1?'':'s'}:</div>
            <div className="max-h-64 overflow-auto border hairline rounded p-2 text-xs">
              {rows.slice(0, 20).map((r, i) => <div key={i} className="border-b hairline py-1 last:border-0">{r['Invoice #'] || '(auto)'} · {r['Client']} · {r['Event Date']} · ₹{r['Total (INR)']}</div>)}
              {rows.length > 20 && <div className="italic text-[var(--muted)] pt-2">…and {rows.length-20} more</div>}
            </div>
            <button className="btn-gold w-full mt-3" onClick={importAll} disabled={creating} data-testid="inv-import-confirm">{creating ? 'Importing…' : `Import ${rows.length}`}</button>
          </div>
        )}
      </div>
    </div>
  );
}
